import { isEmpty, isNil } from 'lodash';

import { ORGANIZATION_PROFILE_TYPE_BRAND } from 'utils/organization';
import { logError } from 'utils/log';
import {
  getMetaForCampaign,
  getMetaForSubcampaign
} from 'utils/trackingMeta';
import { stripQuery } from 'utils/url';
import intercomProvider from '../providers/intercomProvider';
import gaProvider from '../providers/gaProvider';
import { addUserAction } from '../providers/user-action-provider';

import type {
  Account,
  Campaign,
  DeliverableUploadedEventAction,
  DeliverableUploadedEventLabel,
  ListType,
  Organization,
  Subcampaign
} from 'types';
import type { SOVReport } from 'modules/insights/sov/types';

type TrackingPrimitive = string | number;

type TrackingMeta = TrackingPrimitive | Record<string, any>; // which is basically "any"

// Product event types
export const EVENT_TYPE_LOGIN_ATTEMPT = 'login-attempt';
export const EVENT_TYPE_NOTIFICATION_DANGER = 'error';

export const EVENT_TYPE_CAMPAIGN_INVITE_SENT = 'campaign-invite-sent';
export const EVENT_TYPE_CAMPAIGN_INVITE_ACCEPTED = 'campaign-invite-accepted';
export const EVENT_TYPE_CAMPAIGN_INVITE_REJECTED = 'campaign-invite-rejected';
export const EVENT_TYPE_CAMPAIGN_INFLUENCER_INVITED = 'campaign-influencer-invited';
export const EVENT_TYPE_CAMPAIGN_INFLUENCER_REMOVED = 'campaign-influencer-removed';
export const EVENT_TYPE_CAMPAIGN_CREATED = 'campaign-created';
export const EVENT_TYPE_CAMPAIGN_EDITED = 'campaign-edited';

// track only in Google Analytics
export const EVENT_TYPE_SEARCH_FILTERS_UPDATED = 'GA|Search|search-filters-updated';
export const EVENT_TYPE_CONTENT_FILTERS_UPDATED = 'GA|Content Discovery|filters-updated';

export const EVENT_TYPE_MAINTENANCE = 'GA|Maintenance|show-page';

/** Migrated actions */
export const EVENT_TYPE_WMC_GENERATED = 'wmc-generated';
export const EVENT_TYPE_OFFERS_TABLE_CREATE_BUTTON_IN_ROW_CLICKED = 'create-button-in-row-clicked';
export const EVENT_TYPE_OFFERS_TABLE_CREATE_BUTTON_IN_FOOTER_CLICKED = 'create-button-in-footer-clicked';
export const EVENT_TYPE_OFFERS_TABLE_CREATE_MANY_BUTTON_IN_FOOTER_CLICKED = 'create-many-button-in-footer-clicked';
export const EVENT_TYPE_OFFERS_TABLE_CHECKBOX_TOGGLED = 'checkbox-toggled';
export const EVENT_TYPE_OFFERS_TABLE_CHECKBOX_TOGGLED_ALL = 'checkbox-toggled-all';

export const EVENT_TYPE_CAMPAIGN_POST_ADDED = 'campaign-post-added';
export const EVENT_TYPE_CAMPAIGN_POST_REMOVED = 'campaign-post-removed';

export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_ABOUT_CLICKED = 'influencer-profile-tab-about-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_CLICKED = 'influencer-profile-tab-instagram-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_ENGAGEMENT_RATE_CLICKED = 'influencer-profile-tab-instagram-detail-engagement-rate-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_FOLLOWER_GROWTH_CLICKED = 'influencer-profile-tab-instagram-detail-follower-growth-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_TOP_HASHTAGS_CLICKED = 'influencer-profile-tab-instagram-detail-hashtags-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_HISTORY_CLICKED = 'influencer-profile-tab-history-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_CLICKED = 'influencer-profile-tab-youtube-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_ENGAGEMENT_RATE_CLICKED = 'influencer-profile-tab-youtube-detail-engagement-rate-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_FOLLOWER_GROWTH_CLICKED = 'influencer-profile-tab-youtube-detail-follower-growth-clicked';
export const EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_TOP_HASHTAGS_CLICKED = 'influencer-profile-tab-youtube-detail-hashtags-clicked';

const EVENT_TYPE_SOV_REPORT_CREATED = 'sov-report-created';
const EVENT_TYPE_SOV_REPORT_DELETED = 'sov-report-deleted';
const EVENT_TYPE_SOV_REPORT_SAVED = 'sov-report-saved';
const EVENT_TYPE_SOV_ANALYSIS_RUN = 'sov-analysis-run';
const EVENT_TYPE_SOV_ANALYSIS_DOWNLOADED = 'sov-analysis-downloaded';
const EVENT_TYPE_SOV_FETCH_POSTS = 'sov-fetch-posts';
const EVENT_TYPE_SOV_REPORT_DUPLICATED = 'sov-report-duplicated';

// example: consumer-insights uses a token in the url
function stripTokenFromUrl(url: string | undefined | null = ''): string {
  if (!url || isEmpty(url)) {
    return url || '';
  }

  return stripQuery(url);
}

// drops resource ids
// e.g. /campaigns/1392/subcampaigns/6109 -> /campaigns/subcampaigns
const urlResourceIdRegex = /\/\d+/g;
export function generalizeURLForTracking(url: string | undefined | null = ''): string {
  return stripTokenFromUrl(url).replace(urlResourceIdRegex, '');
}

export function trackInitialization(): void {
  try {
    intercomProvider.initialize();
  } catch (error) {
    logError(error);
  }
}

export function trackLogin(email: string) {
  try {
    intercomProvider.trackLogin(email);
  } catch (error) {
    logError(error);
  }
}

export function trackSignUp(createdOrganization: Organization): void {
  try {
    intercomProvider.trackSignUp(createdOrganization);
  } catch (error) {
    logError(error);
  }
}

export function trackEvent(
  eventType: string,
  meta: TrackingMeta | undefined | null = {},
  labelWithoutPrivateData: TrackingPrimitive | undefined | null = null
): void {
  try {
    if (window.app.env.NODE_ENV === 'development') {
      console.log('[Collabary Event]', eventType, meta, labelWithoutPrivateData); // eslint-disable-line no-console
    }

    // `meta` should not be sent to GA because it has private data!
    gaProvider.trackEvent(
      eventType,
      isNil(labelWithoutPrivateData) ? undefined : String(labelWithoutPrivateData)
    );
  } catch (error) {
    logError(error);
  }
}

export function trackPageChanged(
  meta: {
    url: string | undefined | null
  }
): void {
  const updatedMeta = {
    ...meta,
    url: generalizeURLForTracking(meta.url)
  };

  try {
    intercomProvider.trackPageView();
    gaProvider.trackPageView(updatedMeta.url);
  } catch (error) {
    logError(error);
  }
}

export function trackAccountDataUpdated(account: Account, currentOrganization?: Organization): void {
  try {
    intercomProvider.trackAccountDataUpdated(account, currentOrganization);
  } catch (error) {
    logError(error);
  }
}

export function trackOrganizationSwitch(currentAccount: Account, organization: Organization): void {
  try {
    intercomProvider.trackOrganizationSwitch(currentAccount, organization);
  } catch (error) {
    logError(error);
  }
}

export function trackCampaignSwitch(currentAccount: Account, organization: Organization, campaignId: number): void {
  try {
    intercomProvider.trackCampaignSwitch(currentAccount, organization, campaignId);
  } catch (error) {
    logError(error);
  }
}

export function trackLogout(): void {
  try {
    intercomProvider.trackLogout();
  } catch (error) {
    logError(error);
  }
}

// Helpers:

function trackEventSubcampaignChanged(eventType: string, subcampaign: Subcampaign): void {
  const meta = getMetaForSubcampaign(subcampaign);

  trackEvent(eventType, meta);
}

// Product Events:

export function trackEventNotification(
  notification: {
    type: string,
    message: string
  }
): void {
  const { type, message } = notification;

  if (type === 'danger') {
    trackEvent(EVENT_TYPE_NOTIFICATION_DANGER, { message }, message);
  }
}

export function trackEventCampaignCreated(campaign: Campaign): void {
  const meta = getMetaForCampaign(campaign);

  trackEvent(EVENT_TYPE_CAMPAIGN_CREATED, meta);
}

export function trackEventCampaignEdited(campaign: Campaign): void {
  const meta = getMetaForCampaign(campaign);

  trackEvent(EVENT_TYPE_CAMPAIGN_EDITED, meta);
}

export function trackEventCampaignInfluencerInvited(subcampaign: Subcampaign): void {
  trackEventSubcampaignChanged(
    EVENT_TYPE_CAMPAIGN_INFLUENCER_INVITED,
    subcampaign
  );
}

export function trackEventCampaignInfluencerRemoved(subcampaign: Subcampaign): void {
  trackEventSubcampaignChanged(
    EVENT_TYPE_CAMPAIGN_INFLUENCER_REMOVED,
    subcampaign
  );
}

export function trackEventCampaignInviteSent(offerId: number): void {
  trackEvent(
    EVENT_TYPE_CAMPAIGN_INVITE_SENT,
    offerId
  );
}

export function trackEventCampaignInviteAccepted(offerId: number): void {
  trackEvent(
    EVENT_TYPE_CAMPAIGN_INVITE_ACCEPTED,
    offerId
  );
}

export function trackEventCampaignInviteRejected(offerId: number): void {
  trackEvent(
    EVENT_TYPE_CAMPAIGN_INVITE_REJECTED,
    offerId
  );
}

export function trackEventCampaignPostAdded(offerId: number, deliverableId: number): void {
  addUserAction({
    eventCategory: 'campaign-content',
    eventAction: EVENT_TYPE_CAMPAIGN_POST_ADDED,
    eventLabel: `offer.${offerId}|deliverable.${deliverableId}`
  });
}

export function trackEventCampaignPostRemoved(offerId: number, deliverableId: number): void {
  addUserAction({
    eventCategory: 'campaign-content',
    eventAction: EVENT_TYPE_CAMPAIGN_POST_REMOVED,
    eventLabel: `offer.${offerId}|deliverable.${deliverableId}`
  });
}

export function trackEventSimilarCreatorsCriteriaChangeActions(
  relatedInfluencersSearchQuery: {
    [x: string]: boolean
  }
): void {
  const enabledQueries = Object.keys(relatedInfluencersSearchQuery)
    .reduce<Array<string>>((result, key) => {
      if (relatedInfluencersSearchQuery[key]) {
        result.push(key);
      }

      return result;
    }, [])
    .join('+');

  addUserAction({
    eventCategory: 'profile',
    eventAction: 'influencer-profile-similar-creator-criteria-changed',
    eventLabel: enabledQueries
  });
}

export function trackEventProfileActions(eventType: string): void {
  addUserAction({
    eventCategory: 'profile',
    eventAction: 'click',
    eventLabel: eventType
  });
}

export function trackEventProfileCreated(profileType: string): void {
  addUserAction({
    eventCategory: 'profile',
    eventAction: 'profile-created',
    eventLabel: (profileType === ORGANIZATION_PROFILE_TYPE_BRAND) ? 'brand' : 'influencer'
  });
}

export function trackEventProfileUpdated(profileType: string): void {
  addUserAction({
    eventCategory: 'profile',
    eventAction: 'profile-updated',
    eventLabel: (profileType === ORGANIZATION_PROFILE_TYPE_BRAND) ? 'brand' : 'influencer'
  });
}

export function trackEventProfileCoverImageUploaded(profileType: string): void {
  addUserAction({
    eventCategory: 'profile',
    eventAction: 'profile-cover-image-uploaded',
    eventLabel: (profileType === ORGANIZATION_PROFILE_TYPE_BRAND) ? 'brand' : 'influencer'
  });
}

export function trackEventProfileImageUploaded(profileType: string): void {
  addUserAction({
    eventCategory: 'profile',
    eventAction: 'profile-image-uploaded',
    eventLabel: (profileType === ORGANIZATION_PROFILE_TYPE_BRAND) ? 'brand' : 'influencer'
  });
}

export const trackEventListsActions = (eventType: string): void => {
  addUserAction({
    eventCategory: 'collections',
    eventAction: 'tab-selected',
    eventLabel: `${eventType}-collection`
  });
};

export function trackEventSOVReport(type: string, report: Partial<SOVReport>): void {
  const types = {
    created: EVENT_TYPE_SOV_REPORT_CREATED,
    duplicated: EVENT_TYPE_SOV_REPORT_DUPLICATED,
    deleted: EVENT_TYPE_SOV_REPORT_DELETED,
    saved: EVENT_TYPE_SOV_REPORT_SAVED,
    analysis: EVENT_TYPE_SOV_ANALYSIS_RUN,
    analysisDownload: EVENT_TYPE_SOV_ANALYSIS_DOWNLOADED,
    posts: EVENT_TYPE_SOV_FETCH_POSTS
  };

  const meta = {
    brandProfileId: report.brandProfileId,
    reportId: report.id
  };

  const data = `Brand Profile ID: ${meta.brandProfileId || '-'}, Report ID: ${meta.reportId || '-'}`;

  addUserAction({
    eventCategory: 'share-of-voice',
    eventAction: types[type],
    eventLabel: data
  });
}

export const trackEventListsStats = (
  eventType: string,
  listId: number,
  listType: ListType,
  trackingType: string,
  listName: string
): void => {
  addUserAction({
    eventCategory: 'collections',
    eventAction: 'stats-viewed',
    eventLabel: trackingType,
    customDimensionData: {
      collectionInfo: {
        type: listType,
        id: listId,
        name: listName
      }
    }
  });
};

export const trackEventEditCampaign = (triggerType: string): void => {
  addUserAction({
    eventCategory: 'campaigns',
    eventAction: 'campaign-edit',
    eventLabel: triggerType
  });
};

export const trackEventCampaignHeaderExpanded = (page: string): void => {
  addUserAction({
    eventCategory: 'campaigns',
    eventAction: 'campaign-header-expanded',
    eventLabel: page
  });
};

export const trackEventDiscoveryContentFilters = (orgId: number, profileId: number, filtersUpdated?: TrackingMeta): void => {
  trackEvent(EVENT_TYPE_CONTENT_FILTERS_UPDATED, {}, JSON.stringify({
    orgId,
    profileId,
    filtersUpdated
  }));
};

export const trackShippingAddressPopupMenu = (linkType: string): void => {
  addUserAction({
    eventCategory: 'campaigns',
    eventAction: 'address-popup-clicked',
    eventLabel: linkType
  });
};

export const trackLikeAudienceDataWidgetEvent = (action: 'new' | 'cache', campaignId: number, influencerId?: number | null): void => {
  if (action === 'new') {
    addUserAction({
      eventCategory: 'like audience data',
      eventAction: 'new-report',
      eventLabel: `influencer.${influencerId}`
    });
  } else if (action === 'cache') {
    addUserAction({
      eventCategory: 'like audience data',
      eventAction: 'cached-report',
      eventLabel: `influencer.${influencerId}`
    });
  }
};

export const trackEventDeliverableUploaded = (
  eventAction: DeliverableUploadedEventAction,
  eventLabel: DeliverableUploadedEventLabel
): void => {
  addUserAction({
    eventAction,
    eventLabel,
    eventCategory: 'deliverable'
  });
};

export const trackAnalyticScreenshotOverride = (): void => {
  addUserAction({
    eventCategory: 'deliverable',
    eventAction: 'analytic-screenshot-override'
  });
};
